import React from 'react';
import Icon from '../../../components/AppIcon';

const IndustryBenchmarkPanel = () => {
  const benchmarks = [
    {
      category: 'Government Departments',
      avgEmployees: 850,
      avgSavings: 425000,
      avgROI: 485,
      paybackMonths: 2.8,
      adoptionRate: 68,
      topMetrics: [
        { label: 'Compliance Score Improvement', value: '+32%' },
        { label: 'Penalty Cost Reduction', value: '-94%' },
        { label: 'Processing Time Reduction', value: '-78%' }
      ]
    },
    {
      category: 'Provincial Administrations',
      avgEmployees: 420,
      avgSavings: 195000,
      avgROI: 380,
      paybackMonths: 3.2,
      adoptionRate: 54,
      topMetrics: [
        { label: 'Data Accuracy Improvement', value: '+28%' },
        { label: 'Report Generation Time', value: '-96%' },
        { label: 'Budget Variance Reduction', value: '-75%' }
      ]
    },
    {
      category: 'Private Sector (Large)',
      avgEmployees: 1250,
      avgSavings: 680000,
      avgROI: 625,
      paybackMonths: 2.4,
      adoptionRate: 72,
      topMetrics: [
        { label: 'Audit Compliance Rate', value: '+35%' },
        { label: 'Document Retrieval Time', value: '-98%' },
        { label: 'Storage Cost Reduction', value: '-82%' }
      ]
    },
    {
      category: 'Private Sector (SME)',
      avgEmployees: 85,
      avgSavings: 75000,
      avgROI: 320,
      paybackMonths: 3.8,
      adoptionRate: 45,
      topMetrics: [
        { label: 'Compliance Violation Reduction', value: '-88%' },
        { label: 'Payroll Error Reduction', value: '-92%' },
        { label: 'Time Savings', value: '+65%' }
      ]
    }
  ];

  const percentileData = [
    { percentile: '90th', savings: 'K850,000+', roi: '700%+', description: 'Top performers' },
    { percentile: '75th', savings: 'K520,000+', roi: '550%+', description: 'Above average' },
    { percentile: '50th', savings: 'K325,000+', roi: '425%+', description: 'Median' },
    { percentile: '25th', savings: 'K180,000+', roi: '280%+', description: 'Below average' }
  ];

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="TrendingUp" size={28} className="text-emerald-100" />
          <h3 className="text-xl font-bold">PNG Market Benchmarks</h3>
        </div>
        <p className="text-emerald-50 mb-6">
          Industry-wide performance metrics from 50+ PNG organizations using HR automation platforms.
        </p>
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-emerald-100 text-sm mb-1">Avg. Annual Savings</div>
            <div className="text-2xl font-bold">K425,000</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-emerald-100 text-sm mb-1">Avg. ROI</div>
            <div className="text-2xl font-bold">485%</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-emerald-100 text-sm mb-1">Avg. Payback</div>
            <div className="text-2xl font-bold">2.9 mo</div>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <div className="text-emerald-100 text-sm mb-1">Market Adoption</div>
            <div className="text-2xl font-bold">62%</div>
          </div>
        </div>
      </div>
      {/* Percentile Rankings */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Icon name="Award" size={24} className="text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Percentiles</h3>
        </div>
        <div className="space-y-3">
          {percentileData?.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg border border-gray-200 dark:border-slate-600"
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{item?.percentile}</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {item?.description}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Organizations in this percentile achieve these results
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{item?.savings}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{item?.roi} ROI</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Sector Benchmarks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {benchmarks?.map((benchmark, index) => (
          <div
            key={index}
            className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{benchmark?.category}</h4>
              <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-full">
                <Icon name="Users" size={14} className="text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  {benchmark?.adoptionRate}% adoption
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg. Employees</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {benchmark?.avgEmployees?.toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg. Savings</div>
                <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                  K{benchmark?.avgSavings?.toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Avg. ROI</div>
                <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{benchmark?.avgROI}%</div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Payback Period</div>
                <div className="text-lg font-bold text-gray-900 dark:text-white">{benchmark?.paybackMonths} mo</div>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-slate-600 pt-4">
              <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Top Improvements</div>
              <div className="space-y-2">
                {benchmark?.topMetrics?.map((metric, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{metric?.label}</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">{metric?.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Competitive Analysis */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center gap-2 mb-6">
          <Icon name="Target" size={24} className="text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Competitive Advantage</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Zap" size={28} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">3x Faster</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Implementation speed vs traditional systems
            </div>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="Shield" size={28} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">98% Compliance</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Average compliance score across all clients
            </div>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="DollarSign" size={28} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">K425K</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Average annual savings per organization
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryBenchmarkPanel;